import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import Sidebar from './components/common/Sidebar';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { EventClickArg } from '@fullcalendar/core';
import { DateSelectArg } from '@fullcalendar/core/index.js';

import Header from './components/common/Header';
import { createTheme } from '@mui/material/styles';
import BookingModal from './components/booking/BookingModal';
import MyBookingsModal from './components/booking/MyBookingsModal';
// import AppDownloadModal from './components/info/AppDownloadModal';
import UpcomingFeaturesModal from './components/info/UpcomingFeaturesModal';
import ReleaseNoteModal from './components/info/ReleaseNoteModal';
import SubscribeModal from './components/info/SubscribeModal';
import RoomClosureNoticeModal from './components/info/RoomClosureNoticeModal';
import FeedbackFormModal from './components/feedback/FeedbackFormModal';
import { getSession } from './services/supabase';
import { getRooms } from './services/roomService';
import {
  saveBookingsCache,
  readBookingsCache,
  saveRoomsCache,
  readRoomsCache,
  formatCachedTime,
} from './services/offlineCache';
import { getBookings, createBooking, updateBooking, deleteBooking, bookingsToMeetings, RecurrenceDeleteType, isBookingOverlapError } from './services/bookingService';
import { initializeAnalytics, trackPageView, trackEvent, EventCategory, EventAction, trackBookingCreation, trackBookingUpdate, trackBookingDeletion, trackLogout } from './services/analytics';
import { signOut, signIn, getUserProfile, clearUserCaches, USER_AUTO_REFRESH_INTERVAL_MS } from './services/userService';
import { MeetingBooking, MeetingRoom, Booking, User } from './types/models';
import LoginPage from './components/auth/LoginPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import { SUBSCRIBED_CALENDARS, getCalendarConfigByName, CalendarClickAction } from './constants/subscribedCalendars';
import {
  loadEnabledCalendarsFromLocalStorage,
  saveEnabledCalendarsToLocalStorage,
  isRoomClosureNoticeDismissed,
  dismissRoomClosureNotice,
  isRoomClosureNoticeShownToday,
  markRoomClosureNoticeShown
} from './services/userPreferenceService';
import { getClosingRooms, buildClosureNoticeSignature, isRoomClosed } from './utils/roomAvailability';
import { renderCalendarEvent } from './utils/calendarEvents/calendarEventRouter';

export type SnackbarSeverity = 'success' | 'error' | 'info';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1a73e8',
      light: '#4285f4',
      dark: '#1557b0',
    },
    secondary: {
      main: '#ea4335',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#3c4043',
      secondary: '#5f6368',
    },
    divider: '#e8eaed',
    grey: {
      100: '#f1f3f4',
      200: '#e8eaed',
      300: '#dadce0',
      400: '#bdc1c6',
    }
  },
  typography: {
    fontFamily: '"Malgun Gothic", "맑은 고딕", sans-serif',
    button: {
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 4,
  },
});

// 예약 데이터 자동 갱신 주기.
// 1분 주기는 탭을 열어두기만 해도 Supabase egress를 과도하게 소진하므로 5분으로 둔다.
const APP_AUTO_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// 탭 전환이 잦아도 과도한 재조회가 일어나지 않도록 하는 최소 간격
const MIN_BOOKINGS_REFRESH_GAP_MS = 60 * 1000;

// 읽기 전용 상태를 알리는 상단 띠의 높이. 본문이 가려지지 않도록 같은 값만큼 띄운다.
const OFFLINE_STRIP_HEIGHT = 38;

// 자동 갱신 시 다시 받아올 구간.
// 최초 로드는 전체 구간을 받아두고(중복 예약 검사가 메모리의 예약 목록에 의존한다),
// 이후 주기 갱신은 실제로 변경이 일어나는 현재 시점 주변만 받아 해당 구간만 교체한다.
const REFRESH_WINDOW_LOOKBACK_MONTHS = 1;
const REFRESH_WINDOW_LOOKAHEAD_MONTHS = 3;

// 구간 갱신만 반복하면 구간 밖으로 옮겨지거나 삭제된 예약을 따라잡지 못하므로
// 주기적으로 한 번씩 전체를 다시 받아 목록을 맞춘다.
const FULL_REFRESH_INTERVAL_MS = 30 * 60 * 1000;

const getRefreshWindow = () => {
  const now = new Date();
  return {
    from: new Date(now.getFullYear(), now.getMonth() - REFRESH_WINDOW_LOOKBACK_MONTHS, 1),
    to: new Date(
      now.getFullYear(),
      now.getMonth() + REFRESH_WINDOW_LOOKAHEAD_MONTHS + 1,
      0,
      23,
      59,
      59
    ),
  };
};

const AppContent = () => {
  // 애널리틱스 초기화
  useEffect(() => {
    initializeAnalytics();
  }, []);

  // PWA 모드(standalone) 및 모바일 디바이스 감지
  const [isPwaMode, setIsPwaMode] = useState<boolean>(false);
  const [isMobileDevice, setIsMobileDevice] = useState<boolean>(false);
  
  // 모바일 또는 PWA 모드인지 확인
  const isCompactMode = isPwaMode || isMobileDevice;
  
  useEffect(() => {
    // display-mode 미디어 쿼리로 standalone 모드 감지
    const pwaMediaQuery = window.matchMedia('(display-mode: standalone)');
    setIsPwaMode(pwaMediaQuery.matches);
    
    // 모바일 디바이스 감지 (화면 너비가 768px 미만일 때)
    const mobileMediaQuery = window.matchMedia('(max-width: 767px)');
    setIsMobileDevice(mobileMediaQuery.matches);
    
    // 변경 사항 감지를 위한 이벤트 리스너
    const handlePwaChange = (e: MediaQueryListEvent) => {
      setIsPwaMode(e.matches);
    };
    
    const handleMobileChange = (e: MediaQueryListEvent) => {
      setIsMobileDevice(e.matches);
    };
    
    pwaMediaQuery.addEventListener('change', handlePwaChange);
    mobileMediaQuery.addEventListener('change', handleMobileChange);
    
    return () => {
      pwaMediaQuery.removeEventListener('change', handlePwaChange);
      mobileMediaQuery.removeEventListener('change', handleMobileChange);
    };
  }, []);
  const calendarRef = useRef<any>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isMyBookingsModalOpen, setIsMyBookingsModalOpen] = useState(false);
  // const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [isUpcomingFeaturesModalOpen, setIsUpcomingFeaturesModalOpen] = useState(false);
  const [isReleaseNoteModalOpen, setIsReleaseNoteModalOpen] = useState(false);
  const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
  const [isRoomClosureNoticeOpen, setIsRoomClosureNoticeOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [activeEditingBooking, setActiveEditingBooking] = useState<any | undefined>(undefined);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [rooms, setRooms] = useState<MeetingRoom[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  // 서버 조회가 실패해 캐시된 데이터를 보여주는 중인지 나타낸다.
  // null이면 정상, cachedAt이 null이면 서버도 캐시도 없는 상태.
  const [offlineState, setOfflineState] = useState<{ cachedAt: number | null } | null>(null);
  const [offlineNoticeOpen, setOfflineNoticeOpen] = useState(false);
  const [isRetryingConnection, setIsRetryingConnection] = useState(false);
  const isOfflineRef = useRef(false);
  const offlineNoticeShownRef = useRef(false);
  const [eventTooltip, setEventTooltip] = useState<{ x: number; y: number; title: string; time: string; color: string } | null>(null);
  const tooltipTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popoverJustClosed = useRef(false);

  // 구독 캘린더 관련 상태
  const [subscribedCalendarsData, setSubscribedCalendarsData] = useState<Map<string, any[]>>(new Map());
  const [enabledCalendars, setEnabledCalendars] = useState<string[]>(loadEnabledCalendarsFromLocalStorage);

  // 구독 캘린더 선택 상태 localStorage에 저장
  useEffect(() => {
    saveEnabledCalendarsToLocalStorage(enabledCalendars);
  }, [enabledCalendars]);

  // 실제로 표시할 구독 캘린더 목록.
  // 모바일/PWA에서는 사이드바가 표시되지 않아 켜고 끌 방법이 없으므로,
  // 설정과 무관하게 전체를 켜진 것으로 본다.
  const visibleCalendarNames = useMemo(
    () => (isCompactMode ? SUBSCRIBED_CALENDARS.map(calendar => calendar.name) : enabledCalendars),
    [isCompactMode, enabledCalendars]
  );

  // 인증 관련 상태
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRefreshTick, setUserRefreshTick] = useState(0);
  const currentUserLabel = currentUser
    ? `${currentUser.display_name || currentUser.name || currentUser.email || ''}${currentUser.position ? ` ${currentUser.position}` : ''}`
    : '';

  // 인증 상태 확인
  useEffect(() => {
    // 페이지 뷰 이벤트 추적
    trackPageView(window.location.pathname, document.title);
    const checkAuth = async () => {
      try {
        setAuthLoading(true);
        const session = await getSession();
        
        if (session) {
          const user = await getUserProfile();
          if (user) {
            setCurrentUser(user);
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('인증 확인 에러:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;

    const refreshUserContext = async () => {
      // 백그라운드 탭에서는 갱신하지 않는다 (불필요한 트래픽 방지)
      if (document.visibilityState === 'hidden') {
        return;
      }

      try {
        // 사용자 목록 캐시까지 비우면 모달이 전체 사용자 목록을 다시 받아오므로
        // 프로필만 강제로 갱신한다.
        const user = await getUserProfile({ forceRefresh: true });
        if (!cancelled && user) {
          setCurrentUser(user);
          setUserRefreshTick(prev => prev + 1);
        }
      } catch (error) {
        console.error('사용자 정보 자동 갱신 실패:', error);
      }
    };

    const intervalId = window.setInterval(refreshUserContext, USER_AUTO_REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  // 서버 조회가 성공했을 때: 점검 안내를 내리고 복구를 알린다.
  const markServerReachable = useCallback(() => {
    if (!isOfflineRef.current) {
      return;
    }

    isOfflineRef.current = false;
    offlineNoticeShownRef.current = false;
    setOfflineState(null);
    setOfflineNoticeOpen(false);
    setSnackbar({
      open: true,
      message: '서버 연결이 복구되었습니다.',
      severity: 'success',
    });
  }, []);

  // 서버 조회가 실패했을 때: 저장해 둔 예약을 대신 보여주고 점검 안내를 띄운다.
  const markServerUnreachable = useCallback(() => {
    isOfflineRef.current = true;

    const cached = readBookingsCache();
    if (cached) {
      // 화면에 아직 아무 예약도 없을 때만 캐시로 채운다.
      // 이미 보여주고 있는 데이터가 있으면 그게 더 최신이므로 건드리지 않는다.
      setBookings(prev => (prev.length === 0 ? cached.data : prev));
    }

    setOfflineState({ cachedAt: cached ? cached.savedAt : null });

    if (!offlineNoticeShownRef.current) {
      offlineNoticeShownRef.current = true;
      setOfflineNoticeOpen(true);
    }
  }, []);

  // 회의실 정보는 거의 변하지 않으므로 자동 갱신 대상에서 제외하고 최초 1회만 불러온다.
  const fetchRooms = useCallback(async () => {
    try {
      const roomsData = await getRooms();
      setRooms(roomsData);
    } catch (error) {
      console.error('회의실 정보 로드 실패:', error);

      // 서버가 응답하지 않으면 저장해 둔 회의실 목록으로 화면을 유지한다
      const cached = readRoomsCache();
      if (cached) {
        setRooms(prev => (prev.length === 0 ? cached.data : prev));
      }
    }
  }, []);

  // 예약 기간이 끝난 회의실은 더 쓸 수 없으므로 목록에서 뺀다.
  // DB 에서 지우는 것이 아니라 보여주지만 않는 것이라, 그 방으로 잡아 둔 지난 예약은 그대로 남는다.
  const openRooms = useMemo(() => rooms.filter(room => !isRoomClosed(room)), [rooms]);

  // 사용이 끝나가는 회의실 안내.
  // 기간이 걸린 회의실이 모두 끝나면 빈 배열이 되어 안내 자체가 사라진다.
  const closingRooms = useMemo(() => getClosingRooms(rooms), [rooms]);
  const closureNoticeSignature = useMemo(
    () => buildClosureNoticeSignature(closingRooms),
    [closingRooms]
  );

  // 들어올 때마다 한 번 띄운다.
  // 사용 종료가 코앞인 공지라, 확인을 눌렀다고 그 뒤로 영영 숨기면 너무 조용하다.
  // 그만 보고 싶으면 팝업의 '오늘 하루 보지 않기'를 쓰면 된다.
  //
  // 전체 공지이므로 로그인 전에도 띄운다. 다만 비밀번호 재설정은 끝내야
  // 넘어갈 수 있는 흐름이라 방해하지 않는다.
  //
  // ref 는 "이 화면이 떠 있는 동안" 중복으로 뜨지 않게 하는 용도다.
  // (회의실 목록을 다시 불러올 때마다 또 뜨면 성가시다)
  // 새로고침하면 초기화되므로 다음 방문에는 다시 보인다.
  const closureNoticeShownRef = useRef(false);

  useEffect(() => {
    if (closingRooms.length === 0) return;
    if (window.location.pathname === '/reset-password') return;
    if (closureNoticeShownRef.current) return;
    // 로그인 화면에서 띄우고 로그인 직후 또 띄우면 몇 초 사이에 두 번 보게 된다
    if (isRoomClosureNoticeShownToday(closureNoticeSignature)) return;
    if (isRoomClosureNoticeDismissed(closureNoticeSignature)) return;

    closureNoticeShownRef.current = true;
    markRoomClosureNoticeShown(closureNoticeSignature);
    setIsRoomClosureNoticeOpen(true);
  }, [closingRooms, closureNoticeSignature]);

  const lastBookingsRefreshRef = useRef(0);
  const lastFullRefreshRef = useRef(0);

  const refreshBookings = useCallback(
    async (options?: { force?: boolean; full?: boolean }) => {
      const now = Date.now();
      if (!options?.force && now - lastBookingsRefreshRef.current < MIN_BOOKINGS_REFRESH_GAP_MS) {
        return;
      }
      lastBookingsRefreshRef.current = now;

      try {
        // 전체 조회: 중복 예약 검사에 쓰이는 목록을 빠짐없이 채워야 할 때와,
        // 구간 갱신이 놓친 변경을 따라잡기 위해 주기적으로 한 번씩.
        if (options?.full || now - lastFullRefreshRef.current >= FULL_REFRESH_INTERVAL_MS) {
          const all = await getBookings();
          lastFullRefreshRef.current = now;
          setBookings(all);
          markServerReachable();
          return;
        }

        // 주기 갱신: 현재 시점 주변 구간만 받아 그 구간만 교체한다.
        // 구간 밖의 예약은 기존 목록을 그대로 유지해 중복 예약 검사가 깨지지 않게 한다.
        const window = getRefreshWindow();
        const fresh = await getBookings(window);
        const freshIds = new Set(fresh.map(booking => booking.id));

        setBookings(prev => [
          ...prev.filter(
            booking =>
              !freshIds.has(booking.id) &&
              (booking.startTime < window.from || booking.startTime > window.to)
          ),
          ...fresh,
        ]);
        markServerReachable();
      } catch (error) {
        console.error('예약 데이터 갱신 실패:', error);
        markServerUnreachable();
      }
    },
    [markServerReachable, markServerUnreachable]
  );

  // 점검 안내에서 직접 재연결을 시도한다. 복구되면 markServerReachable이 안내를 정리한다.
  const handleRetryConnection = useCallback(async () => {
    setIsRetryingConnection(true);
    try {
      await Promise.all([fetchRooms(), refreshBookings({ force: true, full: true })]);
    } finally {
      setIsRetryingConnection(false);
    }
  }, [fetchRooms, refreshBookings]);

  // 최초 데이터 로드 (인증 여부와 관계없이)
  useEffect(() => {
    fetchRooms();
    refreshBookings({ force: true, full: true });
  }, [fetchRooms, refreshBookings]);

  // 서버에서 받아온 데이터를 캐시에 남겨둔다.
  // 서버가 응답하지 않을 때 이 값으로 조회 화면을 유지한다.
  //
  // 끊긴 동안에는 저장하지 않는다. 캐시에서 복원한 데이터를 그대로 되저장하면
  // 저장 시각이 현재로 갱신되어, 화면이 "언제 기준"인지가 실제와 어긋난다.
  useEffect(() => {
    if (isOfflineRef.current || bookings.length === 0) {
      return;
    }
    saveBookingsCache(bookings);
  }, [bookings]);

  useEffect(() => {
    if (isOfflineRef.current || rooms.length === 0) {
      return;
    }
    saveRoomsCache(rooms);
  }, [rooms]);

  // 구독 캘린더(공휴일, 창립기념일 등)는 값이 거의 바뀌지 않는다.
  // 사용자가 켠 캘린더만, 한 번 받아 두고 다시 받지 않는다.
  // 받지 못한 캘린더는 저장하지 않으므로 다음 갱신 때 다시 시도한다.
  useEffect(() => {
    const pending = SUBSCRIBED_CALENDARS.filter(
      calendar =>
        visibleCalendarNames.includes(calendar.name) && !subscribedCalendarsData.has(calendar.name)
    );

    if (pending.length === 0) {
      return;
    }

    let cancelled = false;

    (async () => {
      const loaded = new Map<string, any[]>();

      for (const calendar of pending) {
        try {
          loaded.set(calendar.name, await calendar.fetchData());
        } catch (error) {
          // 실패를 빈 값으로 저장하면 "없는 것"으로 기억해 다시 불러오지 않는다.
          // 잠깐 끊긴 것뿐인데 그 접속 내내 공휴일이 안 보이게 된다.
          // 저장하지 않고 두면 다음 갱신 때 다시 시도한다.
          console.error(`❌ ${calendar.name} 데이터 로드 실패:`, error);
        }
      }

      // 전부 실패했으면 바꿀 것이 없다. 그대로 두어야 다음에 다시 시도한다.
      if (!cancelled && loaded.size > 0) {
        setSubscribedCalendarsData(prev => {
          const next = new Map<string, any[]>();
          prev.forEach((value, key) => next.set(key, value));
          loaded.forEach((value, key) => next.set(key, value));
          return next;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // userRefreshTick 은 주기적으로 올라간다. 불러오지 못한 캘린더를 다시 시도할 기회로 쓴다.
  }, [visibleCalendarNames, subscribedCalendarsData, userRefreshTick]);

  // 예약 데이터 자동 갱신
  // 백그라운드 탭에서는 건너뛰고, 다시 활성화될 때 한 번 갱신한다.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      refreshBookings();
    }, APP_AUTO_REFRESH_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshBookings();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshBookings]);

  // 예약 데이터는 bookingService에서 MeetingBooking 형식으로 변환

  const [meetings, setMeetings] = useState<MeetingBooking[]>([]);

  useEffect(() => {
    // 이 작업은 예약이 들어올 때, 구독 캘린더가 들어올 때 등 여러 번 돈다.
    // 중간에 기다리는 구간이 있어서, 먼저 시작한 쪽이 나중에 끝나면 그 결과가
    // 최신 결과를 덮어쓴다. 먼저 시작한 쪽은 공휴일이 아직 없던 시점의 값이라
    // 공휴일이 사라져 보인다. 지난 작업은 결과를 반영하지 않게 막는다.
    let cancelled = false;

    const fetchMeetings = async () => {
      try {
        let meetingsData = await bookingsToMeetings(bookings);

        // 활성화된 구독 캘린더 이벤트 추가 (OCP: 새 캘린더 추가 시 자동 처리)
        for (const calendar of SUBSCRIBED_CALENDARS) {
          if (visibleCalendarNames.includes(calendar.name)) {
            const calendarData = subscribedCalendarsData.get(calendar.name) || [];

            const calendarEvents = calendarData.map(event => ({
              id: `${calendar.id}-${event.start}`,
              title: event.title ,
              start: new Date(event.start),
              end: new Date(event.end),
              allDay: event.allDay, // 예약 이벤트와 동일한 렌더링 경로 사용
              backgroundColor: calendar.color,
              borderColor: calendar.borderColor || calendar.color,
              textColor: calendar.textColor || '#ffffff',
              extendedProps: {
                isSubscribed: true, // 구독 이벤트 식별자
                calendarId: calendar.id,
                calendarName: calendar.name,
                roomName: '',
                description: '',
                attendee_uids: [],
                userId: '',
                userName: '',
                userEmail: '',
                is_reminder_enabled: false,
                reminder_minutes: 0,
                parent_booking_id: undefined,
                recurrence_type: undefined,
                recurrence_end_date: undefined
              }
            }));

            meetingsData = [...meetingsData, ...calendarEvents as MeetingBooking[]];
          }
        }

        if (!cancelled) {
          setMeetings(meetingsData);
        }
      } catch (error) {
        console.error('미팅 데이터 변환 오류:', error);
        if (!cancelled) {
          setMeetings([]);
        }
      }
    };

    fetchMeetings();

    return () => {
      cancelled = true;
    };
  }, [bookings, visibleCalendarNames, subscribedCalendarsData, userRefreshTick]);

  useEffect(() => {
    if (!selectedEventId) {
      setActiveEditingBooking(undefined);
      return;
    }

    if (isBookingModalOpen && activeEditingBooking?.id === selectedEventId) {
      return;
    }

    const selectedMeeting = meetings.find(meeting => meeting.id === selectedEventId);
    if (selectedMeeting) {
      setActiveEditingBooking(selectedMeeting as any);
    }
  }, [selectedEventId, meetings, isBookingModalOpen, activeEditingBooking]);

  // 예약 목록을 다시 불러오는 함수
  const fetchBookings = async () => {
    try {
      const bookingsData = await getBookings();
      setBookings(bookingsData);
    } catch (error) {
      console.error('예약 목록 갱신 실패:', error);
    } finally {
      // no loading state
    }
  };

  // SnackbarSeverity 타입에서 warning 제거
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: SnackbarSeverity;
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const pathname = window.location.pathname;
  const isCriticalAuthFlow = pathname === '/login' || pathname === '/reset-password';

  const shouldDeferReload =
    isCriticalAuthFlow ||
    isBookingModalOpen ||
    isMyBookingsModalOpen ||
    isUpcomingFeaturesModalOpen ||
    isReleaseNoteModalOpen ||
    isSubscribeModalOpen ||
    isFeedbackModalOpen;
  // 회의실 안내 팝업은 일부러 뺐다.
  // 여기 넣으면 미뤄 둔 재시작이 '확인'을 누르는 순간 실행되어,
  // 확인을 눌렀더니 팝업이 다시 뜨는 것처럼 보인다. 이 팝업은 입력 중인 내용이
  // 없으므로 재시작에 휩쓸려도 잃을 것이 없다.

  useEffect(() => {
    document.body.dataset.deferAppReload = shouldDeferReload ? 'true' : 'false';

    if (!shouldDeferReload && window.__pendingAppReload) {
      window.dispatchEvent(new Event('app:reload-unblocked'));
    }

    return () => {
      delete document.body.dataset.deferAppReload;
    };
  }, [shouldDeferReload]);

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    if (popoverJustClosed.current) {
      popoverJustClosed.current = false;
      return;
    }
    // 서버와 끊긴 동안에는 예약을 저장할 수 없으므로 입력 화면을 열지 않는다
    if (offlineState) {
      setSnackbar({
        open: true,
        message: '예약 서버 연결이 끊겨 지금은 조회만 됩니다.',
        severity: 'error'
      });
      return;
    }
    setSelectedTimeSlot({
      start: selectInfo.start,
      end: selectInfo.end
    });
    const today = new Date();
    today.setHours(0, 0, 0, 0); // 오늘 00:00:00
    const selectedDate = new Date(selectInfo.start);
    selectedDate.setHours(0, 0, 0, 0);
    
    // 주말인지 확인 (0 = 일요일, 6 = 토요일)
    const dayOfWeek = selectedDate.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  
    // 로그인 상태 확인 후 모달 열기 또는 알림 표시
    if (!isAuthenticated) {
      setSnackbar({
        open: true,
        message: '예약을 생성하기 위해서는 로그인이 필요합니다.',
        severity: 'error'
      });
    } else if (selectedDate < today) {
      setSnackbar({
        open: true,
        message: '오늘 이전의 날짜로는 예약이 불가합니다.',
        severity: 'error'
      });
    } else if (selectedDate > today && isWeekend) {
      setSnackbar({
        open: true,
        message: '주말입니다. 일하지 마시고 가족과 함께 시간을 보내시기 바랍니다.',
        severity: 'error'
      });
    } else {
      setIsBookingModalOpen(true);
    }
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    if (popoverJustClosed.current) {
      popoverJustClosed.current = false;
      return;
    }
    // 구독 이벤트인지 확인
    const isSubscribed = clickInfo.event.extendedProps?.isSubscribed;

    if (isSubscribed) {
      // 구독 이벤트의 경우 clickAction에 따라 처리
      const calendarName = clickInfo.event.extendedProps?.calendarName;
      const calendar = getCalendarConfigByName(calendarName);
      const clickAction = calendar?.clickAction || CalendarClickAction.NONE;

      switch (clickAction) {
        case CalendarClickAction.NONE:
          // 아무 동작 하지 않음
          return;

        case CalendarClickAction.CUSTOM_MODAL:
          // TODO: 커스텀 모달 표시 (미래 구현)
          // const ModalComponent = calendar?.modalComponent;
          // if (ModalComponent) {
          //   setCustomModal({ component: ModalComponent, data: clickInfo.event });
          // }
          return;

        case CalendarClickAction.BOOKING:
          // 예약 모달 표시 (미래 사용 가능성)
          break;

        default:
          // onCustomClick이 있으면 실행
          if (calendar?.onCustomClick) {
            calendar.onCustomClick(clickInfo.event);
          }
          return;
      }
    }

    // 예약 이벤트인 경우 기존 로직 실행
    trackEvent(EventCategory.BOOKING, EventAction.VIEW_BOOKING, clickInfo.event.title);
    setSelectedEventId(clickInfo.event.id);
    setIsBookingModalOpen(true);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      
      // 로그아웃 이벤트 추적
      if (currentUser) {
        trackLogout(currentUser);
      }
      
      // 사용자 인증 상태 초기화
      setIsAuthenticated(false);
      setCurrentUser(null);
      
      // 데이터 다시 불러오기
      try {
        // 회의실/예약 정보 다시 불러오기
        await Promise.all([fetchRooms(), refreshBookings({ force: true, full: true })]);
      } catch (dataError) {
        console.error('데이터 가져오기 실패:', dataError);
      }
      
      setSnackbar({
        open: true,
        message: '로그아웃 되었습니다.',
        severity: 'success'
      });
    } catch (error) {
      console.error('로그아웃 실패:', error);
      setSnackbar({
        open: true,
        message: '로그아웃에 실패했습니다.',
        severity: 'error'
      });
    }
  };

  const handleBookingSubmit = async (booking: { id?: string; title: string; startTime: Date; endTime: Date; roomName: string; description?: string; attendee_uids: string[]; color: string; is_reminder_enabled: boolean; reminder_minutes: number; recurrence_type?: string; recurrence_end_date?: Date | null; }) => {
    try {
      const room = rooms.find(r => r.name === booking.roomName);

      if (!room || !currentUser) {
        throw new Error('회의실 또는 사용자 정보를 찾을 수 없습니다.');
      }
      
      const bookingData = {
        title: booking.title,
        description: booking.description || '',
        roomId: room.id,
        roomName: booking.roomName,
        startTime: booking.startTime,
        endTime: booking.endTime,
        userId: currentUser.id,
        // userEmail 필드 제거 - 데이터베이스에 해당 컬럼이 없음
        attendee_uids: booking.attendee_uids, // attendees에서 attendee_uids로 변경
        color: booking.color,
        is_reminder_enabled: booking.is_reminder_enabled,
        reminder_minutes: booking.reminder_minutes,
        recurrence_type: booking.recurrence_type, // 반복 설정 타입 추가
        recurrence_end_date: booking.recurrence_end_date // 반복 종료 날짜 추가
      };
      
      // 예약 ID가 있는지 확인하여 수정 또는 생성 결정
      if (booking.id) {
        
        // 수정 작업 수행
        const updatedBooking = await updateBooking(booking.id, bookingData);
        
        // 예약 수정 이벤트 추적
        const previousBooking = bookings.find(b => b.id === booking.id);
        if (previousBooking) {
          trackBookingUpdate(updatedBooking, previousBooking);
          
          // 리마인더 설정 변경 추적
          if (previousBooking.is_reminder_enabled !== booking.is_reminder_enabled) {
            trackEvent(
              EventCategory.BOOKING,
              booking.is_reminder_enabled ? EventAction.ENABLE_REMINDER : EventAction.DISABLE_REMINDER,
              booking.title
            );
          }
          
          // 반복 예약 설정 변경 추적
          if (previousBooking.recurrence_type !== bookingData.recurrence_type && bookingData.recurrence_type !== 'NONE') {
            trackEvent(
              EventCategory.BOOKING,
              EventAction.SET_RECURRENCE,
              booking.title,
              undefined,
              { recurrence_type: bookingData.recurrence_type }
            );
          }
        }
        
        // 로컬 상태 업데이트
        setBookings(prev => prev.map(item => 
          item.id === booking.id ? { ...item, ...updatedBooking } : item
        ));
        
        setSnackbar({
          open: true,
          message: '예약이 수정되었습니다.',
          severity: 'success'
        });
      } else {
        // 새 예약 생성
        const createdBooking = await createBooking(bookingData);
        
        // 예약 생성 이벤트 추적
        trackBookingCreation(createdBooking);
        
        // 리마인더 설정 추적
        if (booking.is_reminder_enabled) {
          trackEvent(
            EventCategory.BOOKING,
            EventAction.ENABLE_REMINDER,
            booking.title
          );
        }
        
        // 반복 예약 설정 추적
        if (bookingData.recurrence_type && bookingData.recurrence_type !== 'NONE') {
          trackEvent(
            EventCategory.BOOKING,
            EventAction.SET_RECURRENCE,
            booking.title,
            undefined,
            { recurrence_type: bookingData.recurrence_type }
          );
        }
        
        // 반복 예약이 있는 경우, 모든 예약을 다시 불러오기
        if (booking.recurrence_type && booking.recurrence_type !== 'NONE' && booking.recurrence_end_date) {
          // 모든 예약 다시 불러오기
          const allBookings = await getBookings();
          setBookings(allBookings);
        } else {
          // 반복이 없는 경우 현재 생성된 예약만 추가
          setBookings(prev => [...prev, createdBooking]);
        }
        
        setSnackbar({
          open: true,
          message: '예약이 생성되었습니다.',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('예약 생성 실패:', error);
      // 오류 객체의 세부 정보 출력
      if (error instanceof Error) {
        console.error('오류 메시지:', error.message);
        console.error('오류 스택:', error.stack);
      }
      
      // 사용자에게 더 자세한 오류 메시지 표시
      // 중복 예약은 원인이 분명하므로 안내 문구를 그대로 보여준다
      setSnackbar({
        open: true,
        message: isBookingOverlapError(error)
          ? (error as Error).message
          : `예약 생성에 실패했습니다. 오류: ${error instanceof Error ? error.message : '알 수 없는 오류'}`,
        severity: 'error'
      });
    } finally {
      setIsBookingModalOpen(false);
    }
  };

  const handleBookingDelete = async (bookingId: string, recurrenceDeleteType?: RecurrenceDeleteType) => {
    try {
      // 삭제하기 전에 예약 정보 저장 (UI 업데이트를 위해)
      const bookingToDelete = bookings.find(booking => booking.id === bookingId);
      if (!bookingToDelete) {
        console.error('삭제할 예약을 찾을 수 없습니다:', bookingId);
        return;
      }
      
      // API 호출을 통해 서버에서 삭제
      await deleteBooking(bookingId, recurrenceDeleteType);
      
      // 예약 삭제 이벤트 추적
      if (bookingToDelete) {
        trackBookingDeletion(bookingToDelete, recurrenceDeleteType);
      }
      
      const isPast = bookingToDelete.endTime < new Date();
      const deleteMessage = isPast ? '예약이 삭제되었습니다.' : '예약이 취소되었습니다.';

      // 삭제 유형에 따라 로컬 상태 업데이트
      if (!recurrenceDeleteType || recurrenceDeleteType === 'THIS_EVENT') {
        // 이 일정만 삭제
        setBookings(prev => prev.filter(booking => booking.id !== bookingId));

        setSnackbar({
          open: true,
          message: deleteMessage,
          severity: 'success'
        });
      } else if (recurrenceDeleteType === 'THIS_AND_FOLLOWING') {
        // 이 일정 및 향후 일정 삭제
        const currentStartTime = new Date(bookingToDelete.startTime);
        const parentId = bookingToDelete.parent_booking_id || bookingId;
        
        setBookings(prev => prev.filter(booking => {
          // 현재 예약 제외
          if (booking.id === bookingId) return false;
          
          // 같은 부모를 가진 예약 중 현재 시간 이후의 모든 예약 제외
          if (booking.parent_booking_id === parentId && new Date(booking.startTime) >= currentStartTime) {
            return false;
          }
          
          return true;
        }));
        
        setSnackbar({
          open: true,
          message: '선택한 예약과 향후 반복 예약이 모두 취소되었습니다.',
          severity: 'success'
        });
      } else if (recurrenceDeleteType === 'ALL_EVENTS') {
        // 모든 일정 삭제
        const parentId = bookingToDelete.parent_booking_id || bookingId;
        
        setBookings(prev => prev.filter(booking => {
          // 부모 예약 제외
          if (booking.id === parentId) return false;
          
          // 같은 부모를 가진 모든 예약 제외
          if (booking.parent_booking_id === parentId) return false;
          
          return true;
        }));
        
        setSnackbar({
          open: true,
          message: '모든 반복 예약이 취소되었습니다.',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('예약 삭제 실패:', error);
      setSnackbar({
        open: true,
        message: '예약 삭제에 실패했습니다.',
        severity: 'error'
      });
    } finally {
      setIsBookingModalOpen(false);
    }
  };

  // handleBookingEdit 함수는 handleBookingSubmit으로 통합되었습니다.

  // 로그인 처리 함수
  const handleLogin = async (email: string, password: string) => {
    try {
      setAuthLoading(true);
      // 로그인 전 세션 스토리지 캐시 삭제
      clearUserCaches();
      const result = await signIn(email, password);
      
      if (result.user) {
        // last_sign_in_at이 null이면 첫 로그인 → 비밀번호 재설정 페이지로 이동
        if (!result.last_sign_in_at) {
          setSnackbar({
            open: true,
            message: '최초 로그인입니다. 비밀번호를 변경해주세요.',
            severity: 'info'
          });
          window.location.href = '/reset-password';
          return;
        }
        // last_sign_in_at이 null이 아니면서 password_changed_from_initial이 true인 경우에만 메인으로 이동
        if (result.last_sign_in_at && result.user.password_changed_from_initial) {
          setCurrentUser(result.user);
          setIsAuthenticated(true);
          setSnackbar({
            open: true,
            message: '로그인 성공!',
            severity: 'success'
          });
          window.location.href = '/';
          return;
        }
        // 그 외(비밀번호를 아직 변경하지 않은 경우)
        setSnackbar({
          open: true,
          message: '비밀번호를 변경해야 서비스를 이용할 수 있습니다.',
          severity: 'info'
        });
        window.location.href = '/reset-password';
        return;
      }
    } catch (error: any) {
      console.error('로그인 실패:', error);
      setSnackbar({
        open: true,
        message: `로그인 실패: ${error.message || '알 수 없는 오류'}`,
        severity: 'error'
      });
    } finally {
      setAuthLoading(false);
    }
  };

  // 로딩 중 표시
  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // 비밀번호 재설정 페이지는 인증 상태와 관계없이 접근 가능하도록 함
  if (pathname === '/reset-password') {
    return (
      <ThemeProvider theme={theme}>
        <ResetPasswordPage />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    );
  }
  
  // 로그인 페이지는 /login 경로로 이동
  if (pathname === '/login') {
    return (
      <ThemeProvider theme={theme}>
        <LoginPage onLogin={handleLogin} />

        {/* 전체 공지이므로 로그인 전에도 보여준다. 회의는 말로 먼저 잡기도 한다. */}
        <RoomClosureNoticeModal
          open={isRoomClosureNoticeOpen}
          rooms={closingRooms}
          onClose={() => setIsRoomClosureNoticeOpen(false)}
          onDismissToday={() => dismissRoomClosureNotice(closureNoticeSignature)}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    );
  }

  const fitMorePopoverToViewport = () => {
    const popover = document.querySelector<HTMLElement>('.fc-more-popover');
    if (!popover) return;

    const margin = 12;
    const body = popover.querySelector<HTMLElement>('.fc-popover-body');
    const header = popover.querySelector<HTMLElement>('.fc-popover-header');
    const initialRect = popover.getBoundingClientRect();
    const headerHeight = header?.offsetHeight ?? 36;
    const maxBodyHeight = Math.max(120, window.innerHeight - (margin * 2) - headerHeight);

    popover.style.position = 'fixed';
    popover.style.top = `${initialRect.top}px`;
    popover.style.left = `${initialRect.left}px`;
    popover.style.right = 'auto';
    popover.style.bottom = 'auto';
    popover.style.maxHeight = `${window.innerHeight - (margin * 2)}px`;

    if (body) {
      body.style.maxHeight = `${maxBodyHeight}px`;
      body.style.overflowY = 'auto';
    }

    const rect = popover.getBoundingClientRect();
    const top = Math.min(
      Math.max(margin, rect.top),
      Math.max(margin, window.innerHeight - margin - rect.height)
    );
    const left = Math.min(
      Math.max(margin, rect.left),
      Math.max(margin, window.innerWidth - margin - rect.width)
    );

    popover.style.top = `${top}px`;
    popover.style.left = `${left}px`;
  };
  
  // 로그인 여부와 관계없이 메인 페이지 표시 (인증 필요 없음)

  // 팝업이 떠 있는 동안에는 같은 내용이 두 번 보이지 않도록 띠를 숨긴다
  const isOfflineStripVisible = Boolean(offlineState) && !offlineNoticeOpen;

  // 메인 페이지 표시 (인증 후)
  return (
    <ThemeProvider theme={theme}>
        <Box
          sx={{
            display: 'flex',
            height: '100vh',
            overflow: 'hidden',
            paddingTop: isOfflineStripVisible ? `${OFFLINE_STRIP_HEIGHT}px` : 0,
          }}
        >
          <CssBaseline />
          {isCompactMode && ( //pwa 모드에서는 헤더가 보여야함. 사이드바에서는 안보임.
            <Header
              onLogout={handleLogout}
              onLogin={() => window.location.href = '/login'}
              userName={currentUserLabel}
              isAuthenticated={isAuthenticated}
              hideTitle={isCompactMode}
            />
          )}

          <Sidebar
            rooms={openRooms}
            bookingDisabled={Boolean(offlineState)}
            selectedRoomId={null}
            onRoomSelect={() => { }}
            onNewBooking={() => {
              if (!isAuthenticated) {
                setSnackbar({
                  open: true,
                  message: '예약을 생성하기 위해서는 로그인이 필요합니다.',
                  severity: 'error'
                });
                return;
              }
              setIsBookingModalOpen(true);
            }}
            onMyBookings={() => setIsMyBookingsModalOpen(true)}
            // onAppDownload={() => setIsAppDownloadModalOpen(true)}
            onUpcomingFeatures={() => setIsUpcomingFeaturesModalOpen(true)}
            onReleaseNote={() => setIsReleaseNoteModalOpen(true)}
            onSubscribe={() => setIsSubscribeModalOpen(true)}
            onCustomerService={() => setIsFeedbackModalOpen(true)}
            isAuthenticated={isAuthenticated}
            onLogout={handleLogout}
            onLogin={() => window.location.href = '/login'}
            userName={currentUserLabel}
            enabledCalendars={enabledCalendars}
            onCalendarToggle={(calendarName) => {
              setEnabledCalendars(prev =>
                prev.includes(calendarName)
                  ? prev.filter(name => name !== calendarName)
                  : [...prev, calendarName]
              );
            }}
            setSnackbar={setSnackbar}
          />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              height: '100%',
              overflow: 'hidden',
              mt: isCompactMode ? 6 : 0,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Paper
                elevation={0}
                sx={{
                  height: isCompactMode ? 'calc(100% - 48px)' : '100%',
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5',
                  pt: 0,
                  pl: 0,
                  pr: 2,
                  pb: 2,
                  '.fc': {
                    height: '100%',
                    backgroundColor: '#f5f5f5',
                  },
                  '.fc .fc-toolbar': {
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    marginBottom: '0px',
                    backgroundColor: '#f5f5f5',
                  },
                  '.fc .fc-toolbar-title': {
                    fontSize: '22px',
                    fontWeight: 400,
                    color: theme.palette.text.primary,
                  },
                  '.fc .fc-button': {
                    padding: '5px 12px',
                    fontSize: '13px',
                    fontWeight: 400,
                    boxShadow: 'none !important',
                    textTransform: 'none',
                    border: 'none !important',
                    borderRadius: '6px !important',
                    '&:focus': { boxShadow: 'none !important' },
                  },
                  '.fc .fc-button-primary': {
                    backgroundColor: 'transparent',
                    color: theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      color: theme.palette.text.primary,
                    },
                    '&:disabled': {
                      backgroundColor: 'transparent',
                      color: theme.palette.text.secondary,
                      opacity: 0.4,
                    },
                  },
                  '.fc .fc-button-primary:not(:disabled).fc-button-active': {
                    backgroundColor: 'rgba(0,0,0,0.09)',
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.12)',
                      color: theme.palette.text.primary,
                    },
                  },
                  '.fc .fc-button-group': {
                    gap: '2px',
                  },
                  '.fc .fc-timegrid-slot': {
                    height: '48px',
                    borderColor: theme.palette.grey[200],
                  },
                  '.fc .fc-timegrid-slot-label': {
                    color: '#70757a',
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  },
                  '.fc .fc-col-header-cell': {
                    padding: '0',
                    borderColor: '#ddd',
                    backgroundColor: '#ffffff',
                  },
                  '.fc .fc-col-header-cell-cushion': {
                    padding: '4px 4px',
                    color: '#70757a',
                    fontSize: '13px',
                    fontWeight: 600,
                    textTransform: 'none',
                  },
                  '.fc-theme-standard td, .fc-theme-standard th': {
                    borderColor: '#ddd',
                  },
                  '.fc-theme-standard .fc-scrollgrid': {
                    borderRadius: '0px',
                    overflow: 'hidden',
                  },
                  '.fc-daygrid-day-frame': {
                    backgroundColor: '#ffffff',
                  },
                  '.fc-daygrid-day-top': {
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '8px 0 4px',
                    order: -1,
                    zIndex: 1,
                  },
                  '.fc-daygrid-day-number': {
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#111111',
                    float: 'none',
                    width: '26px',
                    height: '26px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    position: 'relative',
                  },
                  '.fc-day-today .fc-daygrid-day-number': {
                    backgroundColor: '#ff4d4f',
                    color: '#ffffff',
                  },
                  '.fc-highlight': {
                    backgroundColor: '#e8f0fe !important',
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                  },
                  '.fc-daygrid-day-events': {
                    marginTop: 0,
                    position: 'relative',
                    zIndex: 0,
                  },
                  '.fc .fc-day-today': {
                    backgroundColor: '#ffffff !important',
                  },
                  '.fc-more-popover-misc': { // 예시: 만약 12가 이 클래스라면
                    display: 'none',
                  },
                  '.fc-toolbar-title': {
                    fontSize: '22px !important',
                    color: '#3c4043',
                  },
                  '.fc-button': {
                    textTransform: 'none !important',
                  },
                  '.fc-day-sat .fc-daygrid-day-number': {
                    color: '#d32f2f !important'
                  },
                  '.fc-day-sat .fc-col-header-cell-cushion': {
                    color: '#d32f2f !important'
                  },
                  '.fc-day-sun .fc-daygrid-day-number': {
                    color: '#d32f2f !important'
                  },
                  '.fc-day-sun .fc-col-header-cell-cushion': {
                    color: '#d32f2f !important'
                  },
                  '.fc-day-sat .fc-daygrid-day-frame, .fc-day-sun .fc-daygrid-day-frame': {
                    backgroundColor: 'rgba(0,0,0,0.025)',
                  },
                  '.fc-day-past': {
                    opacity: 0.45,
                  },
                  '.fc-more-popover': {
                    opacity: 1,
                    maxWidth: 'min(360px, calc(100vw - 24px))',
                    overflow: 'hidden',
                    zIndex: 10,
                  },
                  '.fc-more-popover .fc-popover-body': {
                    overscrollBehavior: 'contain',
                  },
                  '.fc-event': {
                    borderRadius: '3px',
                    padding: '0',
                    borderWidth: 0,
                    backgroundColor: 'transparent !important',
                    cursor: 'pointer',
                  },
                  '.fc-timegrid-event-harness': {
                    margin: '0',
                    minHeight: '22px',
                  },
                  '.fc-timegrid-event': {
                    backgroundColor: 'transparent !important',
                  },
                  '.fc-more-link': {
                    marginLeft: '4px',
                    fontSize: '11px',
                  },
                  '.fc-daygrid-event': {
                    overflow: 'hidden',
                  },
                  '.fc-event-main': {
                    padding: '0',
                    height: '100%',
                    overflow: 'hidden',
                    display: 'block',
                  },
                  '.fc-event-title': {
                    fontWeight: 400,
                    fontSize: '13px',
                    lineHeight: 1.4,
                  },
                  '.fc-event-time': {
                    fontSize: '13px',
                    opacity: 1,
                    fontWeight: 400,
                  },
                }}>
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  events={meetings}
                  fixedWeekCount={false}
                  // 2. 모바일/PWA에서 오늘 버튼 숨김
                  // 3. 날짜 형식 변경 (2025년 6월 -> 6월)
                  headerToolbar={{
                    left: isCompactMode ? 'prev,next' : 'prev,today,next',
                    center: 'title',
                    right: isCompactMode ? 'dayGridMonth,timeGridDay' : 'dayGridMonth,timeGridWeek,timeGridDay'
                  }}
                  height='100%'
                  expandRows={true} // 시간 슬롯이 사용 가능한 공간을 채우도록 확장 (이것이 중요!)
                  dayHeaderFormat={{ weekday: 'short' }}
                  dayHeaderContent={(arg) => {
                    const viewType = arg.view.type;
                    if (viewType === 'timeGridDay' || viewType === 'timeGridWeek') {
                      const day = arg.date.getDate();
                      const weekday = new Intl.DateTimeFormat('ko-KR', { weekday: 'short' }).format(arg.date);
                      return `${day}(${weekday})`;
                    }
                    return arg.text;
                  }}
                  titleFormat={isCompactMode ? { month: 'long' } : { year: 'numeric', month: 'long' }}
                  buttonIcons={{
                    prev: 'chevron-left',
                    next: 'chevron-right'
                  }}
                  buttonText={{
                    today: '오늘',
                    month: '월',
                    week: '주',
                    day: '일'
                  }}

                  eventContent={renderCalendarEvent}
                  eventMouseEnter={(info) => {
                    if (isCompactMode) return;
                    if (info.view.type !== 'dayGridMonth') return;
                    if (document.querySelector('.fc-more-popover')) return;
                    if (tooltipTimeout.current) clearTimeout(tooltipTimeout.current);
                    const start = info.event.start;
                    const end = info.event.end;
                    if (!start) return;
                    const fmt = (d: Date) => {
                      const h = d.getHours(), m = d.getMinutes();
                      const ampm = h < 12 ? '오전' : '오후';
                      const h12 = h % 12 === 0 ? 12 : h % 12;
                      return `${ampm} ${h12}:${String(m).padStart(2, '0')}`;
                    };
                    const time = end ? `${fmt(start)} ~ ${fmt(end)}` : fmt(start);
                    const rect = info.el.getBoundingClientRect();
                    const color = info.event.borderColor || info.event.backgroundColor || '#1a73e8';
                    setEventTooltip({ x: rect.left + rect.width / 2, y: rect.top - 8, title: info.event.title, time, color });
                  }}
                  eventMouseLeave={() => {
                    tooltipTimeout.current = setTimeout(() => setEventTooltip(null), 100);
                  }}
                  initialView='dayGridMonth'
                  editable={false}
                  selectable={true}
                  selectMirror={true}
                  dayMaxEvents={true} //5주라면 1/5로 균등높이. 4주라면 1/4로.
                  dayMaxEventRows = {5}
                  moreLinkContent={(arg) => {
                    return `${arg.num}개 더보기`;
                  }}
                  moreLinkClick={() => {
                    window.setTimeout(fitMorePopoverToViewport, 0);
                    window.setTimeout(fitMorePopoverToViewport, 50);
                    const onClose = (e: MouseEvent) => {
                      if (!document.querySelector('.fc-more-popover')) return;
                      document.removeEventListener('mousedown', onClose);
                      popoverJustClosed.current = true;
                      setTimeout(() => { popoverJustClosed.current = false; }, 300);
                    };
                    setTimeout(() => document.addEventListener('mousedown', onClose), 100);
                    return 'popover';
                  }}
                  weekends={true}
                  select={isCompactMode ? undefined : handleDateSelect}
                  dateClick={(info) => {
                    if (isCompactMode) return;
                    if (info.view.type !== 'dayGridMonth') return;
                    handleDateSelect({ start: info.date, end: info.date } as any);
                  }}
                  eventClick={handleEventClick}
                  slotMinTime='09:00:00'
                  slotMaxTime='18:00:00'
                  allDaySlot={false}
                  slotDuration='00:30:00'
                  locale='ko'
                  dayCellContent={({ date, view }) => {
                    // 현재 뷰가 'dayGridMonth'일 때만 날짜를 표시
                    if (view.type === 'dayGridMonth') {
                      return date.getDate();
                    }
                    // 'timeGridWeek' 또는 'timeGridDay'일 때는 아무것도 표시하지 않음
                    return null;
                  }}
                />
              </Paper>
            </LocalizationProvider>
          </Box>

          {eventTooltip && (
            <div style={{
              position: 'fixed',
              left: eventTooltip.x,
              top: eventTooltip.y,
              transform: 'translate(-50%, -100%)',
              backgroundColor: '#ffffff',
              color: '#1c1c1e',
              borderRadius: '12px',
              padding: '8px 14px 8px 10px',
              pointerEvents: 'none',
              zIndex: 9999,
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <div style={{ width: '4px', alignSelf: 'stretch', borderRadius: '4px', backgroundColor: eventTooltip.color, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: '11px', color: 'rgba(0,0,0,0.45)', marginBottom: '2px' }}>{eventTooltip.time}</div>
                <div style={{ fontSize: '13px', fontWeight: 600 }}>{eventTooltip.title}</div>
              </div>
            </div>
          )}

          {isCompactMode && isAuthenticated && (
            <Fab
              color="inherit"
              disabled={Boolean(offlineState)}
              onClick={() => {
                const today = new Date();
                today.setHours(9, 0, 0, 0);
                const end = new Date(today);
                end.setHours(10, 0, 0, 0);
                setActiveEditingBooking(undefined);
                setSelectedEventId(null);
                setSelectedTimeSlot({ start: today, end });
                setIsBookingModalOpen(true);
              }}
              sx={{
                position: 'fixed',
                bottom: 28,
                right: 24,
                zIndex: 1200,
                backgroundColor: '#ffffff',
                color: '#1c1c1e',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              <span style={{ fontSize: '28px', lineHeight: 1 }}>+</span>
            </Fab>
          )}

          <BookingModal
            open={isBookingModalOpen}
            onClose={() => {
              setIsBookingModalOpen(false);
              setTimeout(() => {
                setActiveEditingBooking(undefined);
                setSelectedEventId(null);
                setSelectedTimeSlot(null);
              }, 300);
            }}
            onSave={handleBookingSubmit}
            onDelete={handleBookingDelete}
            selectedRoom={null}
            selectedDate={selectedTimeSlot?.start}
            editingBooking={activeEditingBooking}
            rooms={rooms}
            currentUser={currentUser}
            setSnackbar={setSnackbar}
            onRefreshBookings={fetchBookings}
          />

          <MyBookingsModal
            open={isMyBookingsModalOpen}
            onClose={() => setIsMyBookingsModalOpen(false)}
            bookings={meetings.filter(meeting =>
              // 내가 주최한 예약 또는 참석자로 있는 예약
              meeting.extendedProps?.userId === currentUser?.id ||
              meeting.extendedProps?.attendee_uids?.includes(currentUser?.id || '')
            ) as any[]}
            onCancel={handleBookingDelete}
            onEdit={(bookingId) => {
              setSelectedEventId(bookingId);
              setActiveEditingBooking(meetings.find(meeting => meeting.id === bookingId) as any);
              setIsMyBookingsModalOpen(false);
              setIsBookingModalOpen(true);
            }}
            currentUser={currentUser}
          />

          {/* <AppDownloadModal
            open={isAppDownloadModalOpen}
            onClose={() => setIsAppDownloadModalOpen(false)}
          /> */}

          <UpcomingFeaturesModal
            open={isUpcomingFeaturesModalOpen}
            onClose={() => setIsUpcomingFeaturesModalOpen(false)}
          />

          <ReleaseNoteModal
            open={isReleaseNoteModalOpen}
            onClose={() => setIsReleaseNoteModalOpen(false)}
          />

          <SubscribeModal
            open={isSubscribeModalOpen}
            onClose={() => setIsSubscribeModalOpen(false)}
          />

          <RoomClosureNoticeModal
            open={isRoomClosureNoticeOpen}
            rooms={closingRooms}
            onClose={() => setIsRoomClosureNoticeOpen(false)}
            onDismissToday={() => dismissRoomClosureNotice(closureNoticeSignature)}
          />

          <FeedbackFormModal
            open={isFeedbackModalOpen}
            onClose={() => setIsFeedbackModalOpen(false)}
            currentUser={currentUser}
            setSnackbar={setSnackbar}
          />

          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            // 상단 띠가 떠 있으면 그만큼 내려 겹치지 않게 한다
            sx={isOfflineStripVisible ? { mt: `${OFFLINE_STRIP_HEIGHT}px` } : undefined}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* 서버 점검 안내: 연결이 끊긴 것을 처음 확인했을 때 한 번 띄운다 */}
          <Dialog
            open={offlineNoticeOpen}
            onClose={() => setOfflineNoticeOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>예약 서버 연결이 끊겼습니다</DialogTitle>
            <DialogContent>
              {offlineState?.cachedAt ? (
                <>
                  <DialogContentText sx={{ mb: 2 }}>
                    화면은 {formatCachedTime(offlineState.cachedAt)}에 받아온 내용입니다.
                    그 뒤에 생긴 변경은 아직 반영되지 않았습니다.
                  </DialogContentText>
                  <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                    {[
                      { label: '지금 가능', value: '예약 조회' },
                      { label: '복구 후', value: '예약 추가 · 변경 · 취소' },
                    ].map(row => (
                      <Box
                        key={row.label}
                        sx={{
                          display: 'flex',
                          gap: 2,
                          py: 1,
                          fontSize: 14,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box sx={{ width: 64, flex: 'none', color: 'text.secondary' }}>
                          {row.label}
                        </Box>
                        <Box>{row.value}</Box>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                <DialogContentText>
                  저장된 예약이 없어 화면에 표시할 내용이 없습니다.
                </DialogContentText>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleRetryConnection} disabled={isRetryingConnection}>
                {isRetryingConnection ? '연결 확인 중…' : '다시 시도'}
              </Button>
              <Button onClick={() => setOfflineNoticeOpen(false)} variant="contained">
                조회만 계속하기
              </Button>
            </DialogActions>
          </Dialog>

          {/* 팝업을 닫아도 읽기 전용 상태는 화면 위에 계속 남겨둔다 */}
          {isOfflineStripVisible && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: `${OFFLINE_STRIP_HEIGHT}px`,
                zIndex: 1250,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                backgroundColor: '#fbf1d8',
                borderBottom: '1px solid #d9b45c',
                fontSize: 13,
                color: '#3c4043',
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  flex: 'none',
                  borderRadius: '50%',
                  backgroundColor: '#8a5d00',
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {offlineState?.cachedAt
                  ? `예약 서버 연결이 끊겨 지금은 조회만 됩니다. ${formatCachedTime(offlineState.cachedAt)} 기준 화면`
                  : '예약 서버 연결이 끊겨 예약을 불러오지 못했습니다.'}
              </Box>
              <Button
                size="small"
                onClick={handleRetryConnection}
                disabled={isRetryingConnection}
                sx={{ flex: 'none', color: '#8a5d00', fontSize: 12, minWidth: 'auto' }}
              >
                {isRetryingConnection ? '연결 확인 중…' : '다시 시도'}
              </Button>
            </Box>
          )}
        </Box>
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
